export const createBasePinEl = (label: string | undefined, color: string, pinStyle: 'badge' | 'photo' | 'dot' | 'pin', isSelected = false) => {
  if (pinStyle === 'pin') {
    const wrapper = document.createElement('div');
    wrapper.className = 'relative cursor-pointer group map-pin';
    wrapper.style.width = isSelected ? '48px' : '36px';
    wrapper.style.height = isSelected ? '58px' : '44px';
    wrapper.style.filter = isSelected 
      ? 'drop-shadow(0 0 12px rgba(37, 99, 235, 0.6)) drop-shadow(0 6px 8px rgba(0, 0, 0, 0.3))'
      : 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.25))';
    wrapper.style.zIndex = isSelected ? '1000' : '1';
    // No transition on wrapper - Mapbox handles positioning
    if (isSelected) {
      wrapper.dataset.selected = 'true';
    }
    
    // Main pin body - teardrop shape using SVG
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 36 44');
    svg.setAttribute('width', isSelected ? '48' : '36');
    svg.setAttribute('height', isSelected ? '58' : '44');
    svg.style.overflow = 'visible';
    // Only transition transform for hover effect
    svg.style.transition = 'transform 0.15s ease';
    
    // Pin shape path - teardrop
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M18 0C8.06 0 0 8.06 0 18c0 12.6 16.2 25.2 17.4 26.1.4.3.8.4 1.2.3.4 0 .8-.1 1.2-.3C21 42.6 36 30 36 18c0-9.94-8.06-18-18-18z');
    path.setAttribute('fill', color);
    
    // Gradient overlay for depth
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', 'pin-gradient-' + Math.random().toString(36).substr(2, 9));
    gradient.setAttribute('x1', '0%');
    gradient.setAttribute('y1', '0%');
    gradient.setAttribute('x2', '100%');
    gradient.setAttribute('y2', '100%');
    
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('style', 'stop-color:rgba(255,255,255,0.3)');
    
    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('style', 'stop-color:rgba(0,0,0,0.1)');
    
    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    defs.appendChild(gradient);
    svg.appendChild(defs);
    
    const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    overlay.setAttribute('d', 'M18 0C8.06 0 0 8.06 0 18c0 12.6 16.2 25.2 17.4 26.1.4.3.8.4 1.2.3.4 0 .8-.1 1.2-.3C21 42.6 36 30 36 18c0-9.94-8.06-18-18-18z');
    overlay.setAttribute('fill', `url(#${gradient.getAttribute('id')})`);
    
    // Inner white circle
    const innerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    innerCircle.setAttribute('cx', '18');
    innerCircle.setAttribute('cy', '16');
    innerCircle.setAttribute('r', '8');
    innerCircle.setAttribute('fill', 'white');
    
    // Medical cross icon
    const cross = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    cross.setAttribute('d', 'M16.5 12h3v3h3v3h-3v3h-3v-3h-3v-3h3z');
    cross.setAttribute('fill', color);
    
    svg.appendChild(path);
    svg.appendChild(overlay);
    svg.appendChild(innerCircle);
    svg.appendChild(cross);
    
    wrapper.appendChild(svg);
    
    // Add CSS for hover effect if not already present
    if (!document.getElementById('map-pin-styles')) {
      const style = document.createElement('style');
      style.id = 'map-pin-styles';
      style.textContent = `
        .map-pin:hover svg { transform: scale(1.1); }
        .map-pin[data-selected="true"] svg { transform: scale(1.05); }
      `;
      document.head.appendChild(style);
    }
    
    return wrapper;
  }

  if (pinStyle === 'dot') {
    const dot = document.createElement('div');
    dot.className = 'rounded-full shadow-lg cursor-pointer';
    dot.style.width = isSelected ? '20px' : '14px';
    dot.style.height = isSelected ? '20px' : '14px';
    dot.style.backgroundColor = color;
    dot.style.boxShadow = isSelected 
      ? '0 0 12px rgba(37, 99, 235, 0.6), 0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.3)';
    dot.style.border = isSelected ? '3px solid white' : '2px solid white';
    dot.style.zIndex = isSelected ? '1000' : '1';
    // No transition - prevents lag during pan/zoom
    return dot;
  }

  const badge = document.createElement('div');
  badge.className = 'flex items-center justify-center rounded-full text-[10px] font-semibold text-white shadow-lg ring-2 ring-white/50';
  badge.style.width = '32px';
  badge.style.height = '32px';
  badge.style.backgroundColor = color;
  badge.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)';
  badge.textContent = label ?? '';
  return badge;
};

export const createPhotoEl = (photoUrl: string | undefined, name: string | undefined, fallbackText: string, photoSize: number) => {
  const size = Math.max(28, Math.min(160, photoSize));
  const wrapper = document.createElement('div');
  wrapper.className = 'relative overflow-hidden rounded-full ring-2 ring-white shadow';
  wrapper.style.width = `${size}px`;
  wrapper.style.height = `${size}px`;

  if (photoUrl) {
    const img = document.createElement('img');
    img.src = photoUrl;
    img.alt = name ?? 'Preceptor photo';
    img.loading = 'lazy';
    img.referrerPolicy = 'no-referrer';
    img.className = 'object-cover w-full h-full';
    wrapper.appendChild(img);
  } else {
    const initials = document.createElement('span');
    initials.className = 'flex items-center justify-center w-full h-full text-[12px] font-semibold text-white bg-gradient-to-br from-slate-600 to-slate-800';
    initials.textContent = fallbackText;
    wrapper.appendChild(initials);
  }

  return wrapper;
};

export const updatePinElement = (el: HTMLElement, label: string, color: string, pinStyle: 'badge' | 'photo' | 'dot' | 'pin', isSelected = false) => {
  if (pinStyle === 'pin') {
    // Update wrapper styles for selected state
    el.style.width = isSelected ? '48px' : '36px';
    el.style.height = isSelected ? '58px' : '44px';
    el.style.filter = isSelected 
      ? 'drop-shadow(0 0 12px rgba(37, 99, 235, 0.6)) drop-shadow(0 6px 8px rgba(0, 0, 0, 0.3))'
      : 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.25))';
    el.style.zIndex = isSelected ? '1000' : '1';
    
    if (isSelected) {
      el.dataset.selected = 'true';
    } else {
      delete el.dataset.selected;
    }
    
    // For the new SVG-based pin, find and update the path fills
    const svg = el.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', isSelected ? '48' : '36');
      svg.setAttribute('height', isSelected ? '58' : '44');
      
      const mainPath = svg.querySelector('path');
      const innerCircle = svg.querySelector('circle');
      const cross = svg.querySelectorAll('path')[2]; // Third path is the cross
      
      if (mainPath) mainPath.setAttribute('fill', color);
      if (cross) cross.setAttribute('fill', color);
    }
    return;
  }

  if (pinStyle === 'dot') {
    el.style.width = isSelected ? '20px' : '14px';
    el.style.height = isSelected ? '20px' : '14px';
    el.style.backgroundColor = color;
    el.style.boxShadow = isSelected 
      ? '0 0 12px rgba(37, 99, 235, 0.6), 0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.3)';
    el.style.border = isSelected ? '3px solid white' : '2px solid white';
    el.style.zIndex = isSelected ? '1000' : '1';
    return;
  }

  el.style.backgroundColor = color;
  el.textContent = label;
};
